import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { loadBookingEditBooking } from './data-loaders/booking-loader';
import { bookingEditState } from '../client-state/booking-edit-state-context';
import { referenceDateActionTypes } from '../../../redusers/reference-date-reducer';
import { referencePriceModelIdActionTypes } from '../../../redusers/reference-pricemodel-id-reducer';
import { bookingActionTypes } from '../client-state/reducers/bookingReducer';
import { orderReducerActionTypes } from '../../../redusers/order-reducer';
import { customerReducerActionTypes } from '../../../redusers/customer-reducer';
import { paymentReducerActionTypes } from '../../../redusers/payment-reducer';
import { vouchersReducerActionTypes } from '../../../redusers/vouchers-reducer';
import { isWarrantyAppliedActionTypes } from '../../../redusers/is-warranty-applied-reducer';
import { selecetedDateActionTypes } from '../../../redusers/selectedDateReducer';
import { getLang } from '../../../Utils/locale';
import { parse } from 'query-string';
import { SupportedLanguage } from '../../../Utils/graphql-sdk';

const useBookingEditBooking = (enabled: boolean = true) => {
  const { bookingId } = useParams();
  const { dispatch } = useContext(bookingEditState);
  const navigate = useNavigate();
  const language = getLang(parse(window.location.search).lang);

  return useQuery({
    queryKey: ['bookingEditBooking', bookingId, language],
    queryFn: async () => {
      console.log('Running useBookingEditBooking queryFn', bookingId);

      const booking = await loadBookingEditBooking(
        bookingId ?? '',
        language as SupportedLanguage
      );
      if (!booking) throw new Error('No booking');

      // Note: calling the dispatch function here only works because
      // the booking to be edited can't be changed unless a browser refresh is done.
      // Doing anything else than returning data in the queryFn is generally not recommended
      // as the queryFn is only called on initial load and when the cache is refreshed,
      // which is managed by react-query.
      // All in all, this is a little bit of a hack to get the booking data into the client state
      // so it can be manipulated by the user.
      // We might find a better solution in the future.
      dispatch({ type: referenceDateActionTypes.UPDATE_DATE, payload: booking.date });
      dispatch({
        type: referencePriceModelIdActionTypes.UPDATE_PRICEMODEL_ID,
        payload: booking.priceModelId ?? null,
      });

      // Copy server data to client state so we can manipulate it
      const reservationId = booking.reservation?.id || null;
      dispatch({
        type: bookingActionTypes.UPDATE_BOOKING,
        payload: {
          ...booking,
          reservationId,
          lang: booking.lang ?? (language as SupportedLanguage),
        },
      });
      dispatch({
        type: orderReducerActionTypes.UPDATE_ORDER,
        payload: booking.addons,
      });
      dispatch({
        type: customerReducerActionTypes.UPDATE_CUSTOMER,
        payload: booking.customer,
      });
      dispatch({
        type: paymentReducerActionTypes.UPDATE_PAYMENTS,
        payload: booking.payments,
      });
      dispatch({
        type: vouchersReducerActionTypes.UPDATE_VOUCHERS,
        payload: booking.vouchers,
      });

      const isWarrantyApplied = booking.cancellationWarranty ? true : false;
      dispatch({
        type: isWarrantyAppliedActionTypes.SET_IS_APPLIED,
        payload: isWarrantyApplied,
      });

      const isBookingDateInThePast = DateTime.fromISO(booking.date) < DateTime.now();
      if (
        isBookingDateInThePast ||
        !booking.shop.cancellationPolicies ||
        booking.shop.cancellationPolicies.length < 1
      ) {
        navigate(`/booking/${booking.id}/pay?lang=${language}`);
      }

      const localMidnight = DateTime.fromISO(booking.date)
        .setZone(booking.shop.timezoneName)
        .setZone('local', { keepLocalTime: true })
        .startOf('day')
        .toJSDate();

      dispatch({
        type: selecetedDateActionTypes.SELECTED_DATE_UPDATE,
        payload: localMidnight,
      });

      return booking;
    },
    enabled: enabled && Boolean(bookingId),
  });
};

export default useBookingEditBooking;
